import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setFavourite, toggleFavouriteDone } from '../redux/userSlice';
import { setBacklink } from '../redux/backlinkSlice';

const FavouriteIcon = ({ activityId, isBig }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const userId = useSelector(state => state.user.id);
    const { favourites, isLogged } = useSelector(state => state.user);

    // Trovo la entry preferito corrispondente all'attività
    const favouriteEntry = useMemo(
        () => favourites?.find(f => f.activityId === activityId) || null,
        [favourites, activityId]
    );
    const isFavourite = !!favouriteEntry;
    const isDone = favouriteEntry?.isDone === true;
    const isInFavouritesPage = location.pathname === '/favourites';

    const sendSetFavourite = async (toFavourite) => {
        try {
            const token = localStorage.getItem('ticino-wild-token');
            const url = `${process.env.REACT_APP_SERVER_BASE_URL}/users/set-favourite`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ userId, activityId, isFavourite: toFavourite })
            });
            if (response.ok) {
                const result = await response.json();
                dispatch(setFavourite({
                    activityId: result.payload.activityId,
                    isFavourite: result.payload.isFavourite
                }));
            }
        } catch (error) {
            console.log('Favourite error: ', error);
        }
    };

    const sendToggleDone = async (newIsDone) => {
        try {
            const token = localStorage.getItem('ticino-wild-token');
            const url = `${process.env.REACT_APP_SERVER_BASE_URL}/users/toggle-favourite-done`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ activityId, isDone: newIsDone })
            });
            if (response.ok) {
                const result = await response.json();
                dispatch(toggleFavouriteDone({
                    activityId: result.payload.activityId,
                    isDone: result.payload.isDone,
                    doneAt: result.payload.doneAt
                }));
            }
        } catch (error) {
            console.log('Toggle done error: ', error);
        }
    };

    const handleClick = () => {
        // Utente sloggato: porto a login con backlink che instruirà /favourites a effettuare l'add
        if (!isLogged) {
            dispatch(setBacklink(`/favourites?addFav=${activityId}`));
            navigate('/register-login?section=Login');
            return;
        }
        if (isDone) {
            // Stato "fatta": cliccabile solo in /favourites per de-flagga
            if (isInFavouritesPage) {
                sendToggleDone(false);
            }
            return;
        }
        // Stati ⚪/❤️: toggle add/remove standard
        sendSetFavourite(!isFavourite);
    };

    const tooltip = isDone
        ? (isInFavouritesPage ? t('FavouriteIcon.unflagDone') : t('FavouriteIcon.lockedDone'))
        : (isFavourite ? t('FavouriteIcon.remove') : t('FavouriteIcon.add'));

    const isLocked = isDone && !isInFavouritesPage;

    return (
        <div
            className={`
                rounded-full
                ${isBig ? 'w-12 h-12 text-xl border border-[#225424]' : 'w-12 h-12 md:w-6 md:h-6 text-xl md:text-xs'}
                bg-white flex justify-center items-center pt-[5px]
                ${isLocked ? 'cursor-not-allowed' : 'cursor-pointer md:hover:scale-110'}
                transform transition-transform duration-300
                ${isDone ? 'text-[#225424]' : 'text-[#b05704]'}
            `}
            onClick={handleClick}
            title={tooltip}
            aria-label={tooltip}
        >
            {activityId && (
                <div>
                    {isDone && <i className="fi fi-sr-check-circle"></i>}
                    {!isDone && isFavourite && <i className="fi fi-sr-heart"></i>}
                    {!isDone && !isFavourite && <i className="fi fi-rr-heart"></i>}
                </div>
            )}
        </div>
    );
};

export default FavouriteIcon;
